/** Live incoming friend invites (Realtime on `play.invite`), shown as toasts on every lobby page. */
import { FLOAT, GhostButton, LOOK, PrimaryButton } from '@play/ui';
import { useNavigate } from '@tanstack/react-router';
import { Swords } from 'lucide-react';
import { useState } from 'react';
import { answerInvite, useIncomingInvites, useProfileNames } from '../features/play/invites';

/** Invite toasts for the signed-in user. */
export function InviteToasts({ userId }: { userId: string }) {
  const invites = useIncomingInvites(userId);
  const names = useProfileNames(invites.map((i) => i.from_user));
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (invites.length === 0) return null;
  return (
    <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-2 w-[min(360px,calc(100vw-32px))]" aria-live="polite">
      {invites.slice(0, 3).map((inv) => (
        <div key={inv.id} className="rounded-[12px] p-3.5 flex flex-col gap-3 rise" style={FLOAT} role="alert">
          <div className="flex items-center gap-3">
            <span
              className="w-9 h-9 rounded-[9px] shrink-0 inline-flex items-center justify-center"
              style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
            >
              <Swords size={17} />
            </span>
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold truncate" style={{ color: LOOK.text }}>
                {names.data?.[inv.from_user] ?? 'A friend'} invited you
              </p>
              <p className="text-[11.5px]" style={{ color: LOOK.muted }}>
                Tier {inv.tier} · Standard · 1v1
              </p>
            </div>
          </div>
          {error && busy === inv.id ? (
            <p className="text-[12px]" style={{ color: LOOK.bad }}>
              {error}
            </p>
          ) : null}
          <div className="flex gap-2 justify-end">
            <GhostButton
              disabled={busy === inv.id}
              onClick={() => {
                setBusy(inv.id);
                answerInvite(inv.id, false).catch((e: unknown) => {
                  setError(e instanceof Error ? e.message : 'Could not answer.');
                });
              }}
            >
              Decline
            </GhostButton>
            <PrimaryButton
              disabled={busy === inv.id || !inv.room_code}
              onClick={() => {
                setBusy(inv.id);
                answerInvite(inv.id, true)
                  .then(() => {
                    if (inv.room_code) void navigate({ to: '/r/$code', params: { code: inv.room_code } });
                  })
                  .catch((e: unknown) => {
                    setError(e instanceof Error ? e.message : 'Could not answer.');
                  });
              }}
            >
              Join
            </PrimaryButton>
          </div>
        </div>
      ))}
    </div>
  );
}
